import { useState, useEffect, useCallback } from 'react';
import { bookingService } from '../../services/api';
import './AdminTable.css';

const BookingsAdmin = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      // Por ahora cargamos todas las reservas
      // En producción, implementar paginación
      const response = await bookingService.getAll();
      setBookings(response.data.data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      confirmed: '#4caf50',
      cancelled: '#f44336',
      completed: '#2196f3'
    };
    return colors[status] || '#666';
  };

  if (loading) {
    return <div className="admin-loading">Cargando reservas...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gestión de Reservas</h2>
        <div className="stats">
          <span className="stat-item">
            Total: <strong>{bookings.length}</strong>
          </span>
          <span className="stat-item">
            Pendientes: <strong>{bookings.filter(b => b.status === 'pending').length}</strong>
          </span>
        </div>
      </div>

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Cliente</th>
              <th>Vehículo</th>
              <th>Fecha Entrega</th>
              <th>Fecha Devolución</th>
              <th>Total</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(booking => (
              <tr key={booking.id}>
                <td className="mono" style={{ fontSize: '16px', fontWeight: 'bold', color: '#ff6600' }}>
                  {booking.bookingCode || 'N/A'}
                </td>
                <td>{booking.customerInfo?.email || 'N/A'}</td>
                <td>{booking.vehicleName || booking.vehicleId || 'N/A'}</td>
                <td>{new Date(booking.pickupDate).toLocaleDateString('es-AR')}</td>
                <td>{new Date(booking.returnDate).toLocaleDateString('es-AR')}</td>
                <td>$ {booking.totalPrice?.toLocaleString('es-AR')}</td>
                <td>
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: getStatusColor(booking.status) }}
                  >
                    {booking.status}
                  </span>
                </td>
                <td>
                  <button 
                    className="btn-view" 
                    onClick={() => handleViewDetails(booking)}
                  >
                    👁️ Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {bookings.length === 0 && (
          <div className="empty-state">
            No hay reservas registradas aún.
          </div>
        )}
      </div>

      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </div>
  );
};

const BookingDetailsModal = ({ booking, onClose }) => {
  return (
    <div className="form-modal">
      <div className="form-content large">
        <div className="modal-header">
          <h3>Detalles de Reserva</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="details-grid">
          <div className="detail-section">
            <h4>Información General</h4>
            <div className="detail-item">
              <span className="label">ID:</span>
              <span className="value mono">{booking.id}</span>
            </div>
            <div className="detail-item">
              <span className="label">Usuario:</span>
              <span className="value">{booking.userId}</span>
            </div>
            <div className="detail-item">
              <span className="label">Estado:</span>
              <span className="value">{booking.status}</span>
            </div>
            <div className="detail-item">
              <span className="label">Fecha de Creación:</span>
              <span className="value">
                {new Date(booking.createdAt).toLocaleString('es-AR')}
              </span>
            </div>
          </div>

          <div className="detail-section">
            <h4>Fechas y Ubicaciones</h4>
            <div className="detail-item">
              <span className="label">Entrega:</span>
              <span className="value">
                {new Date(booking.pickupDate).toLocaleDateString('es-AR')} - {booking.pickupLocation}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Devolución:</span>
              <span className="value">
                {new Date(booking.returnDate).toLocaleDateString('es-AR')} - {booking.returnLocation}
              </span>
            </div>
          </div>

          <div className="detail-section">
            <h4>Seguros y Adicionales</h4>
            {booking.insurances && Object.keys(booking.insurances).length > 0 ? (
              <div className="detail-item">
                <span className="label">Seguros:</span>
                <span className="value">
                  {Object.entries(booking.insurances)
                    .filter(([_, selected]) => selected)
                    .map(([key]) => key)
                    .join(', ')}
                </span>
              </div>
            ) : (
              <p>Sin seguros adicionales</p>
            )}

            {booking.additionals && Object.keys(booking.additionals).length > 0 && (
              <div className="detail-item">
                <span className="label">Adicionales:</span>
                <span className="value">
                  {Object.entries(booking.additionals)
                    .filter(([_, qty]) => qty > 0)
                    .map(([key, qty]) => `${key}: ${qty}`)
                    .join(', ')}
                </span>
              </div>
            )}
          </div>

          <div className="detail-section">
            <h4>Precio</h4>
            <div className="detail-item">
              <span className="label">Total:</span>
              <span className="value price">
                $ {booking.totalPrice?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Método de Pago:</span>
              <span className="value">{booking.paymentMethod || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingsAdmin;
